import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { WorkorderService } from '../../services/workorder.service';
import { TtmImage } from '../../classes/ttmimage';
import { FormControl, FormGroup } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { Workorder } from '../../classes/workorder';
import { TtmService } from '../../services/ttm.service';
import { UserService } from '../../services/user.service';
import { User } from '../../classes/user';
import { TtmStatus } from '../../classes/ttmstatus';
import { TtmType } from '../../classes/ttmtype';
import { Routenames } from '../../routenames';
import { environment } from '../../../environments/environment.test';

@Component({
    selector: 'app-workorder-images',
    templateUrl: './workorder-images.component.html',
    styleUrls: ['./workorder-images.component.scss'],
})
export class WorkorderImagesComponent implements OnInit, OnDestroy {
    images: TtmImage[] = [];
    filteredImages: TtmImage[] = [];
    loading = false;
    total = 0;

    protected readonly Routenames = Routenames;

    // Filter form
    filterForm = new FormGroup({
        workorderId: new FormControl(null),
        ttmTypeKey: new FormControl<string[]>([]),
        ttmStatusKey: new FormControl<string[]>([]),
        userId: new FormControl(null),
        imageStatusKey: new FormControl<string[]>([]),
        dateFrom: new FormControl(''),
        dateTo: new FormControl(''),
    });

    // Filter options
    users: User[] = [];
    ttmTypes: TtmType[] = [];
    ttmStatuses: TtmStatus[] = [];

    private filterSubscription: Subscription;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private workorderService: WorkorderService,
        private ttmService: TtmService,
        private userService: UserService,
        private titleService: Title,
    ) {}

    isPrinting = false;

    handleBeforePrint = () => {
        this.isPrinting = true;
    };

    handleAfterPrint = () => {
        this.isPrinting = false;
    };

    ngOnInit(): void {
        window.addEventListener('beforeprint', this.handleBeforePrint);
        window.addEventListener('afterprint', this.handleAfterPrint);
        this.titleService.setTitle('TSNed - Werkopdracht Afbeeldingen');

        // Check for workorderId in query params
        this.route.queryParams.subscribe((params) => {
            if (params['workorderId']) {
                this.filterForm.patchValue({
                    workorderId: +params['workorderId'],
                });
            }
        });

        // Load filter options
        this.loadFilterOptions();

        // Load images
        this.loadImages();

        // Subscribe to filter changes
        this.filterSubscription = this.filterForm.valueChanges.pipe(debounceTime(300)).subscribe(() => {
            this.loadImages();
        });
    }

    ngOnDestroy(): void {
        window.removeEventListener('beforeprint', this.handleBeforePrint);
        window.removeEventListener('afterprint', this.handleAfterPrint);

        if (this.filterSubscription) {
            this.filterSubscription.unsubscribe();
        }
    }

    staticMapUrl(image: any) {
        const lat = +image.location.coordinates[1];
        const lng = +image.location.coordinates[0];
        const size = '600x600';
        const zoom = 16;
        const marker = `color:red|${lat},${lng}`;
        return `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=${zoom}&size=${size}&markers=${encodeURIComponent(marker)}&key=${environment.googleMapsKey}`;
    }

    loadFilterOptions(): void {
        // Load users
        this.userService.getAllUsers().subscribe((response) => {
            this.users = response.data;
        });

        // Load TTM types
        this.ttmService.types().subscribe((response) => {
            this.ttmTypes = response.data;

            // default option is announcements
            this.filterForm.patchValue({
                ttmTypeKey: ['announcements'],
            });
        });

        // Load TTM statuses
        this.ttmService.statuses().subscribe((response) => {
            this.ttmStatuses = response.data;
        });
    }

    loadImages(): void {
        this.loading = true;
        const filters = this.filterForm.value;

        const params: any = {};
        if (filters.workorderId) params.workorderId = filters.workorderId;
        if (filters.ttmTypeKey?.length) params.ttmTypeKey = filters.ttmTypeKey;
        if (filters.ttmStatusKey?.length) params.ttmStatusKey = filters.ttmStatusKey;
        if (filters.userId) params.userId = filters.userId;
        if (filters.imageStatusKey?.length) params.imageStatusKey = filters.imageStatusKey;
        if (filters.dateFrom) params.dateFrom = filters.dateFrom;
        if (filters.dateTo) params.dateTo = filters.dateTo;

        this.workorderService.getWorkorderImages(params).subscribe({
            next: (response) => {
                this.images = response.data;
                this.filteredImages = response.data;
                this.total = response.data.length;
                this.loading = false;
            },
            error: (error) => {
                console.error('Error loading images:', error);
                this.loading = false;
            },
        });
    }

    goBack(): void {
        const workorderId = this.filterForm.controls.workorderId.value;
        this.router.navigate(['/', Routenames.WorkOrders], {
            queryParams: workorderId ? {workorderId} : {},
        });
    }

    clearFilters(): void {
        this.filterForm.reset();
    }

    printImages(): void {
        this.isPrinting = true;

        setTimeout(() => {
            window.print();
        }, 1500);
    }
}
